import { extendObservable, action, computed } from 'mobx';
import axios from '../../../libs/axios_csrf';
// useStrict(true);

import AttachmentsStore from '../../common_components/attachments/stores/Attachments';
import GooglePicker from '../../../libs/google_picker';
import BoxPicker from '../../../libs/box_picker';
import BlockStore from './BlockStore';
import { initFiltersFromBmcStages } from '../../common_components/bmc/bmc_functions';
import {
  BMC_BLOCKS,
  BMC_STAGES,
  BMC_STAGES_TO_INTEGER
} from '../../common_components/bmc/bmc_constants';
import { unauthorizedMessageOrProvided } from '../../common_components/alert_helpers';
import AbstractRoot from '../../common_components/stores/AbstractRoot';
import Authorization from './AuthorizationStore';
import BmcCard from './BmcCard';

import SuggestStore from './SuggestStore';

export default class RootStore extends AbstractRoot {
  constructor(
    { bmcId, currentUserPermission, currentUserRoles, userLocale, showStages },
    localeStore
  ) {
    super(
      (currentUserPermission && currentUserPermission.user) || {},
      currentUserRoles,
      localeStore
    );

    this.authorizationStore = new Authorization(this, currentUserPermission);
    this.attachmentsStore = new AttachmentsStore(this);
    this.suggestStore = new SuggestStore(this);

    this.bmcId = bmcId;
    this.belongsToCurrentMvp = false;
    this.boardPreference = {};
    this.thinkingMode = new ThinkingMode({ rootStore: this });
    this.blankSlateMode = false;

    this.googlePicker = new GooglePicker();
    this.boxPicker = new BoxPicker();
    this.oneBlockMode = { active: false, name: BMC_BLOCKS.CUSTOMER_SEGMENTS };

    extendObservable(this, {
      loading: true,
      windowsDimensions: { width: 0, height: 0 },
      currentStage: BMC_STAGES.OBSERVATIONAL,
      secondaryCurrentStage: BMC_STAGES.OBSERVATIONAL,
      showOptionsPanel: false,
      filters: initFiltersFromBmcStages(),
      visionBmcBlocks: [],
      boardableName: '...',
      isBmcVision: false,
      bmcUpdate: 0,
      bmcsFromVp: {},
      currentBlockStep: 6,
      showStages: showStages,
      blocks: [],
      closeStepByStepMode: false,
      stepByStepMode: false
    });
  }

  @action handleStepByStep(state) {
    this.closeStepByStepMode = !state;
    this.stepByStepMode = state;
  }

  @action setShowStages(show) {
    this.showStages = show;
  }

  @action setLoadingAndFetchVisionData() {
    if (this.isVisionBmcLoaded) return;

    const responseCallBack = () => (this.loading = false);
    this.loading = true;
    this.fetchVisionData(responseCallBack);
  }

  @action
  fetchData({ full = true, id = this.bmcId } = {}) {
    axios
      .get(`/bmc_boards/${id}.json`, {})
      .then(
        action(response => {
          if (full) {
            this.boardableName = response.data.boardableName;
            this.isBmcVision = response.data.isBmcVision;
            this.boardPreference = response.data.boardPreference;
            this.currentStage = this.boardPreference.currentStage;
            this.belongsToCurrentMvp = response.data.belongsToCurrentMvp;
            this.bmcsFromVp = response.data.bmcsFromVp;
            this.visionBmcBoardId = response.data.visionBmcBoardId;
            this.thinkingMode.generateStageBmcs();
          }
          // //////////////  must be after currentStage assignation //////////
          this.blocks = [];
          response.data.blocks.forEach(block => {
            this.blocks.push(new BlockStore(this, block));
          });
          // ////////////////////////////////////////////////////////////////
          this.thinkingMode.cacheBmcBlocks(id, this.blocks);

          this.thisMethodShouldBeASideEffect();

          this.loading = false;
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );
  }

  @action
  fetchAndCacheBmcBlocks(id) {
    axios
      .get(`/bmc_boards/${id}.json`, {})
      .then(
        action(response => {
          const blocks = [];
          response.data.blocks.forEach(block => {
            blocks.push(new BlockStore(this, block));
          });
          this.thinkingMode.cacheBmcBlocks(id, blocks);
          this.loading = false;
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );
  }

  @computed
  get isVisionBmcLoaded() {
    return this.visionBmcBlocks.length || this.isBmcVision;
  }

  @action
  fetchVisionData(callback = null) {
    if (this.isVisionBmcLoaded) {
      return;
    }

    axios
      .get(`/bmc_boards/${this.visionBmcBoardId}.json`, {})
      .then(
        action(response => {
          response.data.blocks.forEach(block => {
            this.visionBmcBlocks.push(new BlockStore(this, block));
            if (callback && typeof callback === 'function') callback();
          });
        })
      )
      .catch(
        action(error => {
          if (callback && typeof callback === 'function') callback();
          console.log(error);
        })
      );
  }

  @action
  cloneFromAnotherBmc(fromBmcId) {
    this.loading = true;

    // ------------------  AJAX CALL ---------------
    axios
      .patch(`/bmc_boards/${this.bmcId}/clone_from_another_bmc.json`, {
        bmc_board: { clone_from_bmc_id: fromBmcId }
      })
      .then(
        action(response => {
          // console.log(response);
          this.fetchData();
          this.showOptionsPanel = false;
          this.generateAlert({
            type: 'success',
            headline: 'bmc_clone_success_alert'
          });
        })
      )
      .catch(
        action(error => {
          // console.log(error);
          this.generateAlert({
            type: 'danger',
            headline: unauthorizedMessageOrProvided(
              error,
              'bmc_clone_error_alert'
            )
          });
          this.loading = false;
        })
      );
    //------------------------------------------------
  }

  @computed get emptyBlocks() {
    let noCards = true;
    this.blocks.forEach(block => {
      if (block.cards.length) noCards = false;
    });
    return noCards;
  }

  @computed get isVerySmallWindows() {
    return this.windowsDimensions.width < 700;
  }

  @computed get isCurrentStageFree() {
    return this.currentStage === BMC_STAGES.FREE;
  }

  @action
  toggleShowOptionsPanel() {
    this.showOptionsPanel = !this.showOptionsPanel;
  }

  isValidNewStage(stage) {
    // Mark stages sequentially
    return this.isInmediateNextStage(stage) || this.isInmediatePrevStage(stage);
  }

  isInmediateNextStage(stage) {
    return (
      BMC_STAGES_TO_INTEGER[stage] ===
      BMC_STAGES_TO_INTEGER[this.currentStage] + 1
    );
  }

  isInmediatePrevStage(stage) {
    return (
      BMC_STAGES_TO_INTEGER[stage] ===
      BMC_STAGES_TO_INTEGER[this.currentStage] - 1
    );
  }

  /* TODO: autorun this whenever filters change */

  @action
  thisMethodShouldBeASideEffect = () => {
    Object.assign(
      this.filters,
      ...Object.values(BMC_STAGES)
        .slice(1, BMC_STAGES_TO_INTEGER[this.currentStage])
        .map(k => ({ [k]: true }))
    );
    Object.assign(
      this.filters,
      ...Object.values(BMC_STAGES)
        .slice(BMC_STAGES_TO_INTEGER[this.currentStage])
        .map(k => ({ [k]: false }))
    );
  };

  @action updateStageValues(newStage) {
    const preference = this.lastUsedStagePreference;
    if (this.belongsToCurrentMvp) {
      if (this.isInmediatePrevStage(newStage)) {
        preference[this.currentStage] = false;
      }
      preference[newStage] = true;
      this.currentStage = newStage;
    } else if (preference[newStage] === false) {
      this.generateAlert({
        type: 'danger',
        headline: 'stage_advance_not_current_mvp_error_headline',
        message: 'stage_advance_not_current_mvp_error_message'
      });
    } else {
      /*
                this.generateAlert({
                    type: "warning",
                    headline: "change_stage_not_current_mvp_warning_headline",
                    message: "change_stage_not_current_mvp_warning_message",
                });
                */
      this.currentStage = newStage;
    }
    this.thisMethodShouldBeASideEffect();
  }

  @action
  setCurrentStage(stage, callback) {
    if (!this.isValidNewStage(stage)) {
      this.generateAlert({
        type: 'danger',
        headline: 'invalid_next_stage_alert'
      });
      return;
    }

    this.updateStageValues(stage, this.currentStage);

    // Avoid persisting changes in backend
    // when not in current MVP
    if (!this.belongsToCurrentMvp) {
      return;
    }

    // To show current stage as a card in customer segment block
    const customerSegmentBlock = this.blocks.find(e => {
      return e.name === 'customer_segments';
    });

    if (customerSegmentBlock) {
      const hasNotStageCard =
        customerSegmentBlock.cards.find(
          e =>
            e.description === this.localeStore.intl.formatMessage({ id: stage })
        ) === undefined;
      if (hasNotStageCard) {
        const card = new BmcCard(customerSegmentBlock, {
          description: this.localeStore.intl.formatMessage({ id: stage })
        });
        customerSegmentBlock.addCard(card);
      }
    }
    // this._updateBlocksFilteredCards();

    // TODO: check this after the migration of MVP to react is done
    // must use ajax, because axios doesn't allow server's js response execution
    // this is needed to update mvp current stage
    // Refactoring using Axios convention

    $.ajax({
      type: 'PATCH',
      url: `/bmc_board_preferences/${this.boardPreference.id}.js`,
      data: { bmc_board_preference: { current_stage: this.currentStage } },
      dataType: 'script',
      success: action(response => {
        if (callback) callback();
        // alert('success');
        this.generateAlert({
          type: 'success',
          headline: 'stage_update_success_alert'
        });
      }),
      error: action(response => {
        this.generateAlert({
          type: 'danger',
          headline: unauthorizedMessageOrProvided(
            error,
            'stage_update_error_alert'
          )
        });
      })
    });

    // ------------------  AJAX CALL ---------------
    // axios.patch(`/bmc_board_preferences/${this.boardPreference.id}.js`, {
    //     bmc_board_preference: {current_stage: this.currentStage}
    // }).then((response) => {
    //     //console.log(response);
    // }).catch((error) => {
    //     console.log(error);
    // });

    //------------------------------------------------
  }

  @action
  setStepModeAtIgMvpStore() {
    //  console.log('Here set step bef if: ', this.igMvpRootStore)
    if (this.igMvpRootStore) {
      // console.log('Inside if')
      this.igMvpRootStore.bmcMode = 'step';
    }
  }

  @action
  unsetStepModeAtIgMvpStore() {
    // console.log('Here unset step bef if: ', this.igMvpRootStore)

    if (this.igMvpRootStore) {
      this.igMvpRootStore.bmcMode = 'complete';
    }
  }

  @action
  updateFilters(name, value) {
    this.filters[name] = value;
    // this._updateBlocksFilteredCards();
  }

  @action
  setCurrentStageAndShowBMC(stage) {
    if (!this.thinkingMode.active) {
      return;
    }

    const bmc = this.thinkingMode.stageBmcs[stage];
    if (bmc) {
      this.thinkingMode.prepareAndShowBmc(bmc, stage);
    } else {
      this.generateAlert({
        type: 'warning',
        headline: 'station_change_no_bmc_mvp_warning_alert'
      });
    }

    // let bmc;
    //
    // if (stage === BMC_STAGES.OBSERVATIONAL) {
    //     this.loading = true;
    //     this.currentStage = stage;
    //     this.currentStageDescr = "VISION";
    //     this.thinkingModeAtBmcVision = true;
    //
    //     this.fetchData({full: false, id: this.visionBmcBoardId});
    //
    //     return;
    // }
    //
    // const next = nextStage(stage);
    // if (next !== BMC_STAGES.FREE) {
    //     bmc = this._bmcWithMaxMVP(next) || this._bmcWithMaxMVP(stage);
    // } else {
    //     bmc = this._bmcWithMaxMVP(stage);
    // }
    //
    // if (bmc) {
    //     // console.log("bmc: ", bmc);
    //     this.loading = true;
    //     this.currentStage = stage;
    //     this.currentStageDescr = bmc.name;
    //     this.thinkingModeAtBmcVision = false;
    //
    //     this.fetchData({full: false, id: bmc.id});
    //
    //     return;
    // }
    //
    // this.generateAlert({type: 'warning', headline: 'station_change_no_bmc_mvp_warning_alert'});
  }

  // type is one of: "success", "warning", "danger", "info"
  //     @action
  //     generateAlert({type = "info", timeout = 1500, headline = "", message = ""}) {
  //         this.alert.type = type;
  //         this.alert.timeout = timeout;
  //         this.alert.headline = headline;
  //         this.alert.message = message;
  //
  //         this.showAlert = true;
  //     }

  getBlockStoreByName(name) {
    return this.blocks.find(b => b.name === name);
  }

  @computed get getKeyPropositionBlockStore() {
    return this.getBlockStoreByName(BMC_BLOCKS.KEY_PROPOSITIONS);
  }

  @computed get getCustomerSegmentBlockStore() {
    return this.getBlockStoreByName(BMC_BLOCKS.CUSTOMER_SEGMENTS);
  }

  @computed get getChannelBlockStore() {
    return this.getBlockStoreByName(BMC_BLOCKS.CHANNELS);
  }

  @computed get getCustomerRelationshipBlockStore() {
    return this.getBlockStoreByName(BMC_BLOCKS.CUSTOMER_RELATIONSHIPS);
  }

  @computed get getRevenueStreamBlockStore() {
    return this.getBlockStoreByName(BMC_BLOCKS.REVENUE_STREAMS);
  }

  @computed get getKeyResourcesBlockStore() {
    return this.getBlockStoreByName(BMC_BLOCKS.KEY_RESOURCES);
  }

  @computed get getKeyActivitiesBlockStore() {
    return this.getBlockStoreByName(BMC_BLOCKS.KEY_ACTIVITIES);
  }

  @computed get getKeyPartnersBlockStore() {
    return this.getBlockStoreByName(BMC_BLOCKS.KEY_PARTNERS);
  }

  @computed get getCostStructureBlockStore() {
    return this.getBlockStoreByName(BMC_BLOCKS.COST_STRUCTURE);
  }

  @computed get stepByStepBlock() {
    return this.oneBlockMode.active
      ? this.oneBlockMode.name
      : BMC_BLOCKS.CUSTOMER_SEGMENTS;
  }

  @computed get emphasizedSteps() {
    if (this.thinkingMode.active) {
      return [];
    }

    return [
      'observational_suggests',
      ...Object.values(BMC_STAGES).filter(
        stage => this.lastUsedStagePreference[stage]
      )
    ];
  }

  @computed get firstMarkedStage() {
    if (this.thinkingMode.active) {
      return undefined;
    }

    const firstMarked = Object.values(BMC_STAGES).find(
      stage =>
        this.lastUsedStagePreference[stage] &&
        stage !== BMC_STAGES.OBSERVATIONAL
    );
    return firstMarked;
  }

  @computed get lastMarkedStage() {
    if (this.thinkingMode.active) {
      return undefined;
    }

    const lastMarked = Object.values(BMC_STAGES)
      .reverse()
      .find(stage => this.lastUsedStagePreference[stage]);

    return lastMarked === undefined ? BMC_STAGES.OBSERVATIONAL : lastMarked;
  }

  @computed get secondLastStage() {
    if (this.thinkingMode.active) {
      return false;
    }

    const secondLast = Object.values(BMC_STAGES)
      .reverse()
      .filter(stage => this.lastUsedStagePreference[stage]);

    return secondLast.length > 1 ? secondLast[1] : BMC_STAGES.OBSERVATIONAL;
  }

  canUnmarkStage(stage) {
    return (
      stage == this.firstMarkedStage &&
      stage != this.lastMarkedStage &&
      this.belongsToCurrentMvp
    );
  }

  @action
  handleSelectStage = (stage, proxy, event) => {
    if (
      !this.authorizationStore.canEditStage &&
      !(this.isBmcVision && this.thinkingMode.active)
    ) {
      return;
    }

    if (this.isBmcVision) {
      if (this.thinkingMode.active) {
        this.setCurrentStageAndShowBMC(stage);
      } else {
        this.generateAlert({
          type: 'warning',
          headline: 'station_change_go_business_warning_alert'
        });
      }
      return;
    }

    this.setCurrentStage(stage);
  };

  @action unmarkStage(stage) {
    if (!this.belongsToCurrentMvp) {
      this.generateAlert({
        type: 'warning',
        headline: 'change_stage_not_current_mvp_warning_headline',
        headline: 'change_stage_not_current_mvp_warning_message'
      });
      return;
    }
    const lastUsedPreference = this.lastUsedStagePreference;
    lastUsedPreference[stage] = false;

    axios
      .patch(`/bmc_board_preferences/${lastUsedPreference.id}`, {
        [stage]: false,
        bmc_board_preference: { [stage]: false }
      })
      .then(
        action(response => {
          this.generateAlert({
            type: 'success',
            headline: 'unmark_stage_success'
          });
        })
      )
      .catch(
        action(error => {
       //   console.log('I am here', error);
          lastUsedPreference[stage] = true;
          this.generateAlert({
            type: 'danger',
            headline: unauthorizedMessageOrProvided(error, 'unmark_stage_error')
          });
          this.loading = false;
        })
      );
  }

  @computed get lastUsedStagePreference() {
    return this.bmcsFromVp.find(bmc => bmc.id == this.bmcId)
      .lastUsedStagePreference;
  }

  showLabelForm(label) {
    // return (!this.isBmcVision ||  ( this.isCurrentStageFree && !Object.values(BMC_STAGES).includes(label))) && this.closeStepByStepMode
    return true;
  }

  // toJSON() {
  //     return {
  //         isBmcVision: this.isBmcVision,
  //         boardPreference: this.boardPreference,
  //         currentStage: this.boardPreference.currentStage,
  //         belongsToCurrentMvp: this.belongsToCurrentMvp,
  //         bmcsFromVp: this.bmcsFromVp,
  //         visionBmcBoardId: this.visionBmcBoardId,
  //         blocks: this.blocks,
  //     }
  // }

  // _updateBlocksFilteredCards() {
  //     this.blocks.forEach(b => {
  //         b.updateFilteredCards()
  //     });
  // }
}

class ThinkingMode {
  constructor({ rootStore }) {
    this.rootStore = rootStore;

    this.active = false;
    this.atBmcVision = true;
    this.stageBmcs = {};
  }

  // cache or update cached bmc blocks inside the stageBmcs object by id, for later use
  @action
  cacheBmcBlocks(id, blocks) {
    const bmc = this._getBmcById(id);
    if (bmc) {
      bmc.blocks = blocks;
    }
  }

  // update currentStage (needed to filter cards), update atBmcVision based on stage
  // if cache available use it otherwise fetch from server
  @action
  prepareAndShowBmc(bmc, stage) {
    if (!bmc || !stage) {
      return;
    }

    this.rootStore.loading = true;
    // this.rootStore.currentStage = stage;
    this.rootStore.secondaryCurrentStage = stage;
    // is thinking mode and at bmc vision observational stage
    this.atBmcVision = stage === BMC_STAGES.OBSERVATIONAL;

    // try to pull block from already cached bmc_mvp blocks else fetch from server
    if (bmc.blocks && bmc.blocks.length === 9) {
      this.rootStore.blocks = bmc.blocks;
      this.rootStore.loading = false;
    } else {
      this.rootStore.fetchData({ full: false, id: bmc.id });
    }
  }

  @action
  fetchAllBmcs() {
    const uniqBmcs = [...new Set(Object.values(this.stageBmcs))];

    uniqBmcs.forEach(bmc => {
      if (!bmc.blocks) {
        this.rootStore.fetchAndCacheBmcBlocks(bmc.id);
      }
    });
  }

  // generate object with keys as stages and value as bmc that contain:
  // id, name, currentStage and a possible cached blocks.
  // example  { first_user_suggests: { blocks: [], currentStage:"", id:3, mvpName: ""}, .. }

  // logic for choosing a MVP:
  // check if a MVP exist with currentStage as the nextStage(),  with the purpose of getting the most recent BMC
  // else check using   stage
  // if found any MVP that satisfy any of the conditions, create a new key in  stageBmcs   with the found bmc as a value.
  @action
  generateStageBmcs() {
    Object.values(BMC_STAGES).forEach(stage => {
      // const next = nextStage(stage);
      // let bmc;
      // if (next !== BMC_STAGES.FREE) {
      //     bmc = this._bmcWithMaxMVP(next) || this._bmcWithMaxMVP(stage);
      // } else {
      //     bmc = this._bmcWithMaxMVP(stage);
      // }

      // upper limit stage is the currentStage
      if (
        BMC_STAGES_TO_INTEGER[stage] <=
        BMC_STAGES_TO_INTEGER[this.rootStore.currentStage]
      ) {
        const bmc = this._bmcByLastUsedStageWithMaxMVP(stage);

        if (bmc) {
          this.stageBmcs[stage] = bmc;
        }
      }
    });

    // TODO: TEMP: WHILE MIGRATE to the new data structure ///////////////////////////
    if (Object.values(this.stageBmcs).length < 1) {
      // console.log("Using legacy algorithm to get bmc[]");
      Object.values(BMC_STAGES).forEach(stage => {
        if (
          BMC_STAGES_TO_INTEGER[stage] <=
          BMC_STAGES_TO_INTEGER[this.rootStore.currentStage]
        ) {
          const bmc = this.rootStore.bmcsFromVp.find(
            bmc => bmc.currentStage === stage
          );

          if (bmc) {
            this.stageBmcs[stage] = bmc;
          }
        }
      });
    }
    // ////////////////////////////////////////////////////////////////////////////////////////

    this.stageBmcs[BMC_STAGES.OBSERVATIONAL] = this._getObservationalBmc;

    // console.log("thinking: ", this.thinkingMode.stageBmcs);
  }

  @computed get getStageBmcs() {
    return this.active ? this.stageBmcs : undefined;
  }

  // vision's bmc is set at the observational stage
  // so when clicking on the observational stage it will show the vision's bmc
  @computed get _getObservationalBmc() {
    return {
      currentStage: BMC_STAGES.OBSERVATIONAL,
      id: this.rootStore.visionBmcBoardId,
      mvpName: 'VISION'
    };
  }

  // get (if exist) the bmc with the max MVP number from  bmcsFromVp
  // must be used with a populated bmcsFromVp
  _bmcByLastUsedStageWithMaxMVP(stage) {
    const result = this.rootStore.bmcsFromVp.filter(
      bmc => bmc.lastUsedStagePreference[stage]
    );
    if (result.length) {
      return result.reduce((a, b) =>
        this._mvpNameToInteger(a.mvpName) > this._mvpNameToInteger(b.mvpName)
          ? a
          : b
      );
    }
  }

  _getBmcById(id) {
    return Object.values(this.stageBmcs).find(bmc => bmc.id === id);
  }

  _mvpNameToInteger(name) {
    const num = name.match(/\d+/);
    if (num) {
      return Number(num[0]);
    }
    return -1;
  }
}
